import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { activityService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { Card, Button, Field, Input, Select, Textarea, Spinner } from '../../components/ui';
import { cleanSocialLinks, SocialLinksEditor } from '../../components/SocialLinks';
import useBrandingStore from '../../store/brandingStore';
import { applyBrandTheme, DEFAULT_THEME, THEME_PRESETS } from '../../utils/theme';

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'DZD', label: 'Algerian Dinar (DZD)' },
];

function AdminSchoolSettings() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [form, setForm] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const setBranding = useBrandingStore((state) => state.setBranding);
  const previewPrimaryColor = form?.primary_color;
  const previewSidebarColor = form?.sidebar_color;

  useEffect(() => {
    activityService.getSettings()
      .then((res) => setForm({
        ...res.data,
        social_links: res.data.social_links || {},
        primary_color: res.data.primary_color || DEFAULT_THEME.primary_color,
        sidebar_color: res.data.sidebar_color || DEFAULT_THEME.sidebar_color,
      }))
      .catch(() => addNotification(t('Failed to load school settings'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (previewPrimaryColor && previewSidebarColor) {
      applyBrandTheme({
        primary_color: previewPrimaryColor,
        sidebar_color: previewSidebarColor,
      });
    }
  }, [previewPrimaryColor, previewSidebarColor]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        site_name: form.site_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        address: form.address,
        terms_of_service: form.terms_of_service,
        social_links: cleanSocialLinks(form.social_links),
        primary_color: form.primary_color,
        sidebar_color: form.sidebar_color,
        default_currency: form.default_currency,
        landing_celebration_enabled: Boolean(form.landing_celebration_enabled),
      };
      if (logoFile) payload.logo = logoFile;
      const res = await activityService.updateSettings(payload);
      setForm(res.data);
      setLogoFile(null);
      setLogoPreview(null);
      setBranding(res.data);
      addNotification(t('School settings saved'), 'success');
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save settings'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('School Settings')} subtitle={t('Name, logo, contact info, and defaults shown across the platform')} />
        <main className="flex-1 overflow-y-auto p-6">
          {loading || !form ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : (
            <Card className="max-w-4xl space-y-5">
              <Field label={t('School Name')}>
                <Input value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} />
              </Field>

              <Field label={t('Logo')}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                    {logoPreview || form.logo ? (
                      <img src={logoPreview || form.logo} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs text-gray-400">{t('No logo')}</span>
                    )}
                  </div>
                  <input type="file" accept="image/*" ref={fileInputRef} onChange={handleLogoChange} className="hidden" />
                  <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>{t('Upload Logo')}</Button>
                </div>
              </Field>

              <div className="border-t border-surface-200 pt-5">
                <h2 className="font-display text-lg font-bold text-gray-900">{t('Platform colors')}</h2>
                <p className="mb-5 mt-1 text-sm text-gray-500">
                  {t('Choose a ready-made palette or customize the primary and sidebar colors.')}
                </p>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {THEME_PRESETS.map((preset) => {
                    const selected = form.primary_color?.toUpperCase() === preset.primary_color
                      && form.sidebar_color?.toUpperCase() === preset.sidebar_color;
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          primary_color: preset.primary_color,
                          sidebar_color: preset.sidebar_color,
                        })}
                        aria-pressed={selected}
                        className={`rounded-2xl border p-3 text-left transition rtl:text-right ${
                          selected
                            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-100'
                            : 'border-surface-200 bg-white hover:border-primary-300 hover:shadow-card'
                        }`}
                      >
                        <div className="mb-3 flex h-10 overflow-hidden rounded-xl">
                          <span className="w-2/5" style={{ backgroundColor: preset.sidebar_color }} />
                          <span className="flex-1" style={{ backgroundColor: preset.primary_color }} />
                          <span className="w-1/5 bg-surface-100" />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-800">{t(preset.name)}</span>
                          {selected && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">✓</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label={t('Primary color')} hint={t('Used for buttons, links and highlights')}>
                    <label className="flex items-center gap-3 rounded-xl border border-surface-200 bg-white p-3">
                      <input
                        type="color"
                        value={form.primary_color}
                        onChange={(event) => setForm({ ...form, primary_color: event.target.value.toUpperCase() })}
                        className="h-10 w-14 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                        aria-label={t('Primary color')}
                      />
                      <span className="font-mono text-sm font-semibold text-gray-700">{form.primary_color}</span>
                    </label>
                  </Field>
                  <Field label={t('Sidebar color')} hint={t('Used as the navigation sidebar background')}>
                    <label className="flex items-center gap-3 rounded-xl border border-surface-200 bg-white p-3">
                      <input
                        type="color"
                        value={form.sidebar_color}
                        onChange={(event) => setForm({ ...form, sidebar_color: event.target.value.toUpperCase() })}
                        className="h-10 w-14 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                        aria-label={t('Sidebar color')}
                      />
                      <span className="font-mono text-sm font-semibold text-gray-700">{form.sidebar_color}</span>
                    </label>
                  </Field>
                </div>

                <div className="mt-5 overflow-hidden rounded-2xl border border-surface-200 bg-surface-50 shadow-card">
                  <div className="flex min-h-24">
                    <div className="w-24 p-3 text-white" style={{ backgroundColor: form.sidebar_color }}>
                      <div className="mb-3 h-5 w-10 rounded bg-white/25" />
                      <div className="mb-2 h-2 w-14 rounded bg-white/60" />
                      <div className="h-2 w-10 rounded bg-white/30" />
                    </div>
                    <div className="flex flex-1 items-center justify-between gap-3 p-5">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{t('Live preview')}</p>
                        <p className="mt-1 text-xs text-gray-500">{t('Your palette is previewed immediately.')}</p>
                      </div>
                      <span className="rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm">
                        {t('Primary action')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-surface-200 pt-5">
                <h2 className="font-display text-lg font-bold text-gray-900">{t('Landing page celebration')}</h2>
                <p className="mb-4 mt-1 text-sm text-gray-500">
                  {t('Control the five-second countdown, opening gift and confetti shown on the public principal page.')}
                </p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(form.landing_celebration_enabled)}
                  onClick={() => setForm({
                    ...form,
                    landing_celebration_enabled: !form.landing_celebration_enabled,
                  })}
                  className="flex w-full items-center justify-between gap-4 rounded-2xl border border-surface-200 bg-surface-50 p-4 text-left transition hover:border-primary-200 rtl:text-right"
                >
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">
                      {form.landing_celebration_enabled ? t('Celebration enabled') : t('Celebration disabled')}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">
                      {form.landing_celebration_enabled
                        ? t('Visitors will see the celebration when opening the vitrine.')
                        : t('Visitors will go directly to the vitrine without an animation.')}
                    </span>
                  </span>
                  <span className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.landing_celebration_enabled ? 'bg-primary-600' : 'bg-gray-300'}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${form.landing_celebration_enabled ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-1 rtl:-translate-x-1'}`} />
                  </span>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label={t('Contact Email')}>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                </Field>
                <Field label={t('Contact Phone')}>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
                </Field>
              </div>

              <Field label={t('Address')}>
                <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              </Field>

              <Field label={t('Default Currency')} hint={t('Used to pre-fill the currency when a teacher creates a new course')}>
                <Select value={form.default_currency} onChange={(e) => setForm({ ...form, default_currency: e.target.value })}>
                  {CURRENCIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </Select>
              </Field>

              <Field label={t('Terms of Service')}>
                <Textarea rows={6} value={form.terms_of_service} onChange={(e) => setForm({ ...form, terms_of_service: e.target.value })} />
              </Field>

              <div className="border-t border-surface-200 pt-5">
                <h2 className="font-display text-lg font-bold text-gray-900">{t('Platform social media')}</h2>
                <p className="mb-5 mt-1 text-sm text-gray-500">{t('These links are displayed in the platform footer and public pages.')}</p>
                <SocialLinksEditor value={form.social_links} onChange={(social_links) => setForm({ ...form, social_links })} />
              </div>

              <div className="flex justify-end pt-2">
                <Button loading={saving} onClick={save}>{t('Save Settings')}</Button>
              </div>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminSchoolSettings;
